import React from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import theme from '@adsk/alloy-react-theme';

import useResizePanel, { UseResizePanelProps } from './useResizePanel';
import Panel, { PanelProps } from '../../Panel';
import { ResizeDivider } from '../../ResizeDivider';

const StyledPanel = styledC(Panel)<{ minWidth?: number }>`
  border-right: 1px solid ${theme.colors.charcoal100};
  box-shadow: ${(props) =>
    props.minWidth !== undefined
      ? `0 0 0 1px ${theme.colors.charcoal200}`
      : undefined};
  max-width: 100%;
  min-width: ${(props) =>
    props.minWidth !== undefined ? `${props.minWidth}px` : undefined};
`;

const StyledDiv = styledC.div`
  padding: 0;
  display: flex;
  position: relative;
  max-width: 100%;
`;

export type ResizePanelProps = UseResizePanelProps &
  Omit<PanelProps, 'width' | 'onResize'>;

const ResizePanel = React.forwardRef<HTMLDivElement, ResizePanelProps>(
  (
    {
      open,
      width,
      openRight,
      onResize,
      children,
      resizeAffordance = 16,
      minWidth,
      maxWidth,
      closedWidth,
      style,
      ...props
    },
    ref,
  ) => {
    const { getResizeDividerProps, getPanelProps } = useResizePanel({
      width,
      minWidth,
      maxWidth,
      openRight,
      onResize,
      open,
      resizeAffordance,
    });

    return (
      <StyledDiv>
        {open && <ResizeDivider {...getResizeDividerProps()} />}
        <StyledPanel ref={ref} css={style} {...getPanelProps(props)}>
          {children}
        </StyledPanel>
      </StyledDiv>
    );
  },
);

ResizePanel.displayName = 'ResizePanel';

ResizePanel.propTypes = {
  /** If panel is open or not */
  open: PropTypes.bool,
  /** If panel is opens to the right or not */
  openRight: PropTypes.bool,
  /** Panel content */
  children: PropTypes.any,
  /** Width of Panel.  Use Panel.WIDTHS for standard widths */
  width: PropTypes.number,
  /** Width of Resize Affordance */
  resizeAffordance: PropTypes.number,
  /** Called whenever the panel resizes */
  onResize: PropTypes.func,
  /** Minimum width allowed by resize */
  minWidth: PropTypes.number,
  /** Maximum width allowed by resize */
  maxWidth: PropTypes.number,
  /** If panel is closed it will retain this size */
  closedWidth: PropTypes.number,
};

export default ResizePanel;
